// OWNER: CONTENT agent. Placeholder rules for campaign scripts, shared by the dialer UI, the recorder and tests.
// Pure TypeScript, no runtime dependencies, safe to import from the Worker or the web app.
//
// Two kinds of blanks appear in script text:
//   {curly}   filled automatically by the app (see PLACEHOLDERS below).
//   [square]  a cue the setter fills by eye from the screen, e.g. "[time]" from the Book panel. Never auto-filled.

export const PLACEHOLDERS = {
  setter: "the setter's name: full name on US leads (SC, VA and ND need first and last), first name on Canadian leads",
  business: 'the business name',
  dm_name: "the decision-maker's name (business.dmName)",
  observation: 'the verified observation (only when observationVerifiedAt is set)',
  callback: 'our callback number (settings.main_number_id, else the caller ID on this call)',
  first_name: "the decision-maker's first name, for emails",
  calendly_link: 'the prefilled setter booking link',
  mailing_address: "Valistix Inc.'s mailing address (CASL and CAN-SPAM need it in every email)",
} as const;

export type Placeholder = keyof typeof PLACEHOLDERS;
export type PlaceholderValues = Partial<Record<Placeholder, string | null | undefined>>;

const TOKEN = /\{([a-z_]+)\}/g;

/** Every {placeholder} name used in a piece of text, in order of first use, without duplicates. */
export function placeholdersIn(text: string): string[] {
  const seen: string[] = [];
  for (const m of text.matchAll(TOKEN)) {
    if (!seen.includes(m[1])) seen.push(m[1]);
  }
  return seen;
}

/**
 * Fill {placeholders}. Anything without a non-empty value is left in place and reported in `missing`,
 * so the caller can hide that variant (e.g. the observation opener when there is no verified observation)
 * instead of showing a setter a half-filled line. Unknown names are reported too.
 */
export function fillPlaceholders(text: string, values: PlaceholderValues): { text: string; missing: string[] } {
  const missing: string[] = [];
  const out = text.replace(TOKEN, (whole, name: string) => {
    const v = (values as Record<string, string | null | undefined>)[name];
    if (typeof v === 'string' && v.trim() !== '') return v.trim();
    if (!missing.includes(name)) missing.push(name);
    return whole;
  });
  return { text: out, missing };
}

/** True when every {placeholder} in the text has a value. Use it to pick which opener or email to show. */
export function canFill(text: string, values: PlaceholderValues): boolean {
  return fillPlaceholders(text, values).missing.length === 0;
}
